<template>
  <v-text-field
    class="platform-text-field"
    v-bind="attrs"
    v-on="on"
    v-model="inputValue"
    ref="input"
    :type="inputType"
    :readonly="readonly"
    :label="required ? `${label || ''} *` : label"
    :rules="validationRules"
    :outlined="outlined"
    :solo="solo"
    :flat="flat"
    :loading="loading"
    :disabled="disabled"
    :prepend-icon="prependIcon"
    :append-icon="appendIcon"
    :placeholder="placeholder"
    :persistent-placeholder="persistentPlaceholder"
    :autofocus="autofocus"
    @focus="$emit('focus')"
    @paste="handlePaste"
  >
    <input-label slot="label" :label="label" :required="required" />
    <template
      v-if="$slots.append || showLock"
      #append
    >
      <slot name="append" />
      <v-icon
        v-if="showLock"
        small
        role="button"
        tabindex="0"
        class="platform-text-field__lock-icon"
        :aria-label="computedLockAriaLabel"
        @click="handleLockClick"
        @keydown.enter.prevent="handleLockClick"
        @keydown.space.prevent="handleLockClick"
      >
        {{ locked ? 'mdi-lock' : 'mdi-lock-open' }}
      </v-icon>
    </template>
    <template
      v-if="$slots.default"
      #default
    >
      <slot
        name="default"
      />
    </template>
  </v-text-field>
</template>

<script lang="ts">
import Vue from 'vue'
import InputLabel from '@/components/shared/inputs/InputLabel.vue'
import { InputFieldMixin } from '@/components/mixins/InputField'

export default Vue.extend({
  name: 'PlatformTextField',
  components: {
    InputLabel
  },
  mixins: [InputFieldMixin],
  props: {
    value: { type: String, required: false },
    inputType: { type: String, default: 'text' },
    autofocus: { type: Boolean, default: false },
    showLock: { type: Boolean, default: false },
    locked: { type: Boolean, default: true },
    lockAriaLabel: { type: String, default: '' },
    onLockToggle: { type: Function, required: false }
  },
  computed: {
    computedLockAriaLabel (): string {
      if (this.lockAriaLabel) {
        return this.lockAriaLabel
      }
      return this.locked ? 'Unlock field' : 'Lock field'
    }
  },
  methods: {
    handleLockClick (): void {
      if (typeof this.onLockToggle === 'function') {
        this.onLockToggle()
      }
      this.$emit('lock-toggle')
    },
    handlePaste (): void {
      const originalValue = this.value
      setTimeout(() => {
        const input = this.$refs.input as any
        const currentValue = input?.$refs?.input?.value ?? input?.lazyValue
        if (currentValue !== undefined && currentValue !== originalValue) {
          this.$emit('input', currentValue)
        }
      }, 0)
    }
  }
})
</script>

<style lang="scss" scoped>
.platform-text-field__lock-icon {
  cursor: pointer;

  &:hover,
  &:focus {
    opacity: 0.7;
  }

  &:focus {
    outline: 2px solid currentColor;
    outline-offset: 2px;
    border-radius: 2px;
  }
}
</style>
